/**
 * Pure state machine for architect hub readiness.
 *
 * Cold login sequence (real QA failure mode):
 * 1. Auth settles → CRM shell `loading` false, timberProjects still [].
 * 2. users/{uid} snapshot may arrive **from cache** without
 *    `architectHubProjectIds` (or with []). Must NOT mark ready yet.
 * 3. Server user doc arrives with real ids → still not ready until each
 *    timberProjects/{id} has reported at least once.
 * 4. Then ready (projects + later taskGroups hydrate).
 */

export type ArchitectHubHydrationState = {
  /** True once we have a non-cached user doc snapshot (ids list is authoritative). */
  userDocFromServer: boolean;
  expectedIds: string[];
  reportedIds: string[];
  ready: boolean;
};

export type ArchitectHubHydrationEvent =
  | { type: 'reset' }
  | { type: 'user_doc'; projectIds: string[]; fromCache: boolean }
  | { type: 'project_doc'; projectId: string };

export function initialArchitectHubHydrationState(): ArchitectHubHydrationState {
  return {
    userDocFromServer: false,
    expectedIds: [],
    reportedIds: [],
    ready: false,
  };
}

function uniqueSorted(ids: string[]): string[] {
  return Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean))).sort();
}

function allExpectedReported(expectedIds: string[], reportedIds: string[]): boolean {
  if (!expectedIds.length) return true;
  const reported = new Set(reportedIds);
  return expectedIds.every((id) => reported.has(id));
}

/**
 * Reduce hub hydration events. `ready` means:
 * - user doc has been seen from the server (not cache-only), AND
 * - either architectHubProjectIds is genuinely empty, OR
 * - every expected project id has received at least one project-doc snapshot.
 */
export function reduceArchitectHubHydration(
  state: ArchitectHubHydrationState,
  event: ArchitectHubHydrationEvent,
): ArchitectHubHydrationState {
  if (event.type === 'reset') {
    return initialArchitectHubHydrationState();
  }

  if (event.type === 'user_doc') {
    const expectedIds = uniqueSorted(event.projectIds);
    // New id list → drop prior project reports (listeners are re-bound).
    const next: ArchitectHubHydrationState = {
      userDocFromServer: state.userDocFromServer || !event.fromCache,
      expectedIds,
      reportedIds: [],
      ready: false,
    };

    // Cache snapshot with empty ids is not authoritative — keep waiting.
    if (event.fromCache && expectedIds.length === 0) {
      return { ...next, userDocFromServer: false, ready: false };
    }

    // Cache snapshot with ids: remember expected ids but wait for server user doc
    // before treating empty→ready, and wait for project docs either way.
    if (event.fromCache) {
      return { ...next, userDocFromServer: false, ready: false };
    }

    // Server user doc: empty hub is a real empty grant list.
    if (expectedIds.length === 0) {
      return { ...next, userDocFromServer: true, ready: true };
    }

    return {
      ...next,
      userDocFromServer: true,
      ready: false,
    };
  }

  // project_doc
  if (!event.projectId.trim()) return state;
  const reportedIds = uniqueSorted([...state.reportedIds, event.projectId]);
  const ready =
    state.userDocFromServer &&
    state.expectedIds.length > 0 &&
    allExpectedReported(state.expectedIds, reportedIds);

  return {
    ...state,
    reportedIds,
    ready,
  };
}
